import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { Currency, HireUnitType, Measurement } from "@/gql/graphql";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { MeasurementInput } from "@/app/Common/Components/MeasurementInput";
import { HireUnitTypeInput } from "@/app/Common/Components/HireUnitType";
import { useTranslations } from "use-intl";
import { Cog6ToothIcon } from "@heroicons/react/20/solid";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { gapRentalConfigurationValidation } from "@/app/GapRentals/Validation/gapRentalValidation";

/**
 * A gap rental never departs on a route, so it has no latest departure date and
 * no ferry to book — its configuration is just the regional settings.
 */
type GapRentalConfigurationFormValues = {
  currency: Currency;
  measurement: Measurement;
  hire_unit_type: HireUnitType;
};

function GapRentalConfigurationFormFields() {
  const tForm = useTranslations("relocation.form");

  return (
    <div className="col-span-full grid grid-cols-3 gap-4 sm:grid-cols-6">
      <CurrencyTypeInput
        name="currency"
        label={tForm("currency")}
        className="col-span-3"
      />
      <MeasurementInput
        name="measurement"
        label={tForm("measurement")}
        className="col-span-3"
      />
      <HireUnitTypeInput
        name="hire_unit_type"
        label={tForm("hireUnit")}
        className="col-span-3"
      />
    </div>
  );
}

function GapRentalConfigurationViewState({
  record,
}: {
  record: GapRentalConfigurationFormValues;
}) {
  const tForm = useTranslations("relocation.form");
  const t = useTranslations("relocation");

  return (
    <dl className="col-span-full grid grid-cols-2 gap-4 text-sm">
      <div>
        <dt className="text-gray-500">{tForm("currency")}</dt>
        <dd className="font-medium">{record.currency}</dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("measurement")}</dt>
        <dd className="font-medium">{record.measurement}</dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("hireUnit")}</dt>
        <dd className="font-medium">
          {t(`hire_unit_type_capitalized.${record.hire_unit_type}`)}
        </dd>
      </div>
    </dl>
  );
}

export function GapRentalConfigurationFormCard() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateRelocationMutation, {
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const record = useRelocationRecord();
  const tForm = useTranslations("relocation.form");

  const initialValues: GapRentalConfigurationFormValues = {
    currency: record.currency,
    measurement: record.measurement,
    hire_unit_type: record.hire_unit_type,
  };

  return (
    <RecordScreenFormCard<GapRentalConfigurationFormValues>
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title={
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-orange-100 p-2">
            <Cog6ToothIcon className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <div>{tForm("configurationTitle")}</div>
            <p className="text-sm font-normal text-gray-500">
              {tForm("configurationDescription")}
            </p>
          </div>
        </div>
      }
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(
        gapRentalConfigurationValidation,
      )}
      view={<GapRentalConfigurationViewState record={initialValues} />}
      onSubmit={async (values) => {
        await mutateAsync({
          id: record.id,
          input: {
            currency: values.currency,
            measurement: values.measurement,
            hire_unit_type: values.hire_unit_type,
          },
        });
      }}
    >
      <GapRentalConfigurationFormFields />
    </RecordScreenFormCard>
  );
}
